import {
  QueryFunction,
  QueryKey,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import ForbiddenError from 'api/errors/ForbiddenError';
import UnauthorizedError from 'api/errors/UnauthorizedError';
import useAuth from './useAuth';

function useProtectedQuery<TData = unknown, TError = unknown>(
  key: QueryKey,
  queryFn: QueryFunction<TData>,
  config?: UseQueryOptions<TData, TError>,
): UseQueryResult<TData, TError> {
  const { triggerUnauthorized, triggerForbidden, isLoggedIn, isForbidden } =
    useAuth();

  const extendedConfig: UseQueryOptions<TData, TError> = {
    retry: 1,
    ...config,
    enabled:
      config?.enabled !== undefined
        ? config?.enabled && isLoggedIn && !isForbidden
        : isLoggedIn && !isForbidden,
    onError: (error: TError) => {
      if (error instanceof UnauthorizedError) {
        triggerUnauthorized();
        return;
      }
      if (error instanceof ForbiddenError) {
        triggerForbidden();
        return;
      }

      config?.onError?.(error);
    },
  };

  return useQuery<TData, TError>(key, queryFn, extendedConfig);
}

export default useProtectedQuery;
